import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { X, ChevronLeft, ChevronRight, ImagePlus, StickyNote, Timer, Save, Activity, Eye, HeartHandshake, Users, Target, Repeat, ClipboardCheck, Award, Search } from "lucide-react";

const IIEM = "/logos/master_wordmark.webp";
const MUAY = "/logos/emblem_muaythai_sm.webp";
const DOJOVA = "/brand/dojova-logo-light.png";
export const INK = "#2A2A2E", RED = "#D90429", GOLD = "#C9A227", PAPER = "#FAF7F2";

/** Emplacement photo : photo réelle de Ralph, sinon image générée (badge), sinon cadre « Photo à fournir ». Jamais d'image Internet. */
export function PhotoSlot({ slot, photos, defaults, label, className = "", fit = "cover" }) {
  const url = photos?.[slot];
  const gen = !url && defaults?.[slot];
  if (url || gen) return (
    <div className={`relative ${className}`} data-testid={`amikobi-photo-${slot}`}>
      <img src={url || gen} alt={label} className={`w-full h-full rounded-[28px] ${fit === "contain" ? "object-contain" : "object-cover"}`} />
      {gen && <span className="absolute bottom-3 right-3 rounded-full px-3 py-1 text-[11px] tracking-wide bg-black/55 text-white" data-testid={`amikobi-photo-generated-${slot}`}>Image générée — à remplacer par une photo réelle</span>}
    </div>
  );
  return (
    <div className={`rounded-[28px] border-2 border-dashed flex flex-col items-center justify-center text-center p-6 ${className}`} style={{ borderColor: "#d6cfc4", background: "linear-gradient(135deg,#f3eee6,#ece4d8)", color: "#8a8178" }} data-testid={`amikobi-photo-${slot}`}>
      <ImagePlus size={40} strokeWidth={1.4} />
      <div className="mt-3 text-sm font-semibold uppercase tracking-[.18em]">Photo à fournir</div>
      <div className="mt-1 text-base max-w-[22ch]">{label}</div>
    </div>
  );
}

/** Plaque anthracite pour les logos officiels (fichiers blancs conçus pour fond sombre — jamais recréés ni déformés). */
function LogoPlate({ big = false }) {
  return (
    <div className={`inline-flex w-fit items-center gap-4 rounded-2xl ${big ? "px-7 py-5" : "px-4 py-2.5"}`} style={{ background: INK }} data-testid="amikobi-logo-plate">
      <img src={MUAY} alt="Muay Thaï — Institut Impact Élite Martial" className={big ? "h-28" : "h-14"} />
      <img src={IIEM} alt="Institut Impact Élite Martial" className={big ? "h-28" : "h-14"} />
    </div>
  );
}

export const CANVAS = { w: 1600, h: 900 };

function Frame({ children, dark = false, footer, n, total }) {
  return (
    <div className="flex flex-col overflow-hidden" style={{ width: CANVAS.w, height: CANVAS.h, background: dark ? INK : PAPER, color: dark ? "#f5f1ea" : INK }}>
      <div className="h-2 shrink-0" style={{ background: RED }} />
      <div className="flex-1 relative px-20 py-10 min-h-0">{children}</div>
      <div className="flex items-center justify-between px-20 pb-5 text-[13px] tracking-wide shrink-0" style={{ color: dark ? "#b8b2a8" : "#7a736a" }}>
        <span>{footer}</span>
        <span>Institut Impact Élite Martial · Programme éducatif et sportif Amikobi · {n}/{total}</span>
      </div>
    </div>
  );
}

const T = "font-['Clash_Display',_'Cabinet_Grotesk',_sans-serif]";
const B = "font-['Satoshi',_'Manrope',_sans-serif]";

function Cover({ photos, defaults, n, total }) {
  return (
    <Frame n={n} total={total} footer="Rencontre du 21 octobre 2026 — École Amikobi, Lac-Simon">
      <div className="h-full grid grid-cols-[1.1fr_1fr] gap-16 items-center">
        <div>
          <LogoPlate big />
          <div className={`${B} mt-9 text-[15px] uppercase tracking-[.3em]`} style={{ color: RED }}>Programme éducatif et sportif Amikobi</div>
          <h1 className={`${T} mt-3 font-semibold leading-[1.02] text-[88px]`} data-testid="amikobi-slide-title">Bouger. Apprendre.<br /><span style={{ color: RED }}>Grandir ensemble.</span></h1>
          <div className="mt-9 flex items-center gap-4">
            <div className="h-px w-14 shrink-0" style={{ background: GOLD }} />
            <p className={`${B} text-[17px] leading-snug`} style={{ color: "#5e5750" }}>Avec le soutien de la plateforme DOJOVA<br />pour le suivi des apprentissages</p>
            <img src={DOJOVA} alt="DOJOVA" className="h-8" />
          </div>
        </div>
        <PhotoSlot slot="couverture" photos={photos} defaults={defaults} label="Grande photo de formation — jeunes en action" className="w-full h-[640px]" />
      </div>
    </Frame>
  );
}

const BENEFITS = [
  { w: "Corps", Icon: Activity, t: "Coordination, équilibre, endurance, maîtrise de soi" },
  { w: "Attention", Icon: Eye, t: "Écouter, mémoriser, s'arrêter au signal" },
  { w: "Confiance", Icon: HeartHandshake, t: "Des objectifs adaptés, des progrès visibles" },
  { w: "Relations", Icon: Users, t: "Coopérer, respecter, s'entraider" },
];

function Benefits({ n, total }) {
  return (
    <Frame n={n} total={total} footer="Pistes encourageantes dans la recherche — pas une garantie de résultats scolaires">
      <div className="h-full flex flex-col">
      <div className="flex items-start justify-between">
        <div>
          <div className={`${B} text-sm uppercase tracking-[.3em]`} style={{ color: RED }}>Ce que les jeunes gagnent</div>
          <h1 className={`${T} mt-3 font-semibold leading-tight text-[64px]`} data-testid="amikobi-slide-title">Corps · Attention · Confiance · Relations</h1>
        </div>
        <LogoPlate />
      </div>
      <div className="mt-12 grid grid-cols-4 gap-8">
        {BENEFITS.map(({ w, Icon, t }, i) => (
          <div key={w} className="rounded-[28px] bg-white p-9 flex flex-col justify-start gap-6 shadow-[0_18px_50px_-30px_rgba(42,42,46,.35)]" style={{ borderTop: `6px solid ${i === 3 ? GOLD : RED}` }} data-testid={`amikobi-benefit-${i}`}>
            <div className="w-20 h-20 rounded-2xl flex items-center justify-center" style={{ background: "#fbeaec", color: RED }}><Icon size={42} strokeWidth={1.6} /></div>
            <div className={`${T} text-[44px] font-semibold`}>{w}</div>
            <p className={`${B} text-[24px] leading-snug`} style={{ color: "#5e5750" }}>{t}</p>
          </div>
        ))}
      </div>
      </div>
    </Frame>
  );
}

const STEPS = [
  { w: "Observer", Icon: Search }, { w: "Un objectif", Icon: Target }, { w: "Pratiquer", Icon: Repeat }, { w: "Évaluer", Icon: ClipboardCheck }, { w: "Reconnaître", Icon: Award, gold: true },
];

function Progression({ photos, defaults, n, total }) {
  return (
    <Frame n={n} total={total} footer="Trois parcours, sans hiérarchie : découverte et mieux-être · apprentissage · développement sportif volontaire">
      <div className="flex items-start justify-between">
        <div>
          <div className={`${B} text-sm uppercase tracking-[.3em]`} style={{ color: RED }}>Progression</div>
          <h1 className={`${T} mt-3 font-semibold leading-tight text-[64px]`} data-testid="amikobi-slide-title">Un objectif. De la pratique.<br />Un progrès <span style={{ color: GOLD }}>reconnu.</span></h1>
        </div>
        <LogoPlate />
      </div>
      <div className="mt-12 grid grid-cols-[1.3fr_1fr] gap-14 items-center">
        <div>
          <div className="relative flex justify-between items-start">
            <div className="absolute left-8 right-8 top-8 h-1 rounded-full" style={{ background: "#e4ddd2" }} />
            {STEPS.map(({ w, Icon, gold }, i) => (
              <div key={w} className="relative flex flex-col items-center w-[150px] text-center" data-testid={`amikobi-step-${i}`}>
                <div className="w-16 h-16 rounded-full flex items-center justify-center text-white shadow-lg" style={{ background: gold ? GOLD : RED }}><Icon size={28} strokeWidth={1.8} /></div>
                <div className={`${T} mt-4 text-[24px] font-semibold leading-tight`}>{w}</div>
              </div>
            ))}
          </div>
          <p className={`${B} mt-12 text-[24px] leading-snug`} style={{ color: "#5e5750" }}>Chaque progrès est noté dans le carnet du jeune. <b style={{ color: INK }}>Un jeune peut réussir sans jamais combattre.</b></p>
        </div>
        <PhotoSlot slot="reussite" photos={photos} defaults={defaults} label="Entraide ou moment de réussite" className="w-full h-[420px]" />
      </div>
    </Frame>
  );
}

const money = (n) => `${Number(n || 0).toFixed(2).replace(".", ",")} $`;

function Generic({ s, photos, defaults, n, total, sim, eff }) {
  const rows = s.kind === "budget" ? [["Effectif", `${eff} jeunes · ${sim.cours_total} cours / ${sim.semaines} semaines`], ["Forfait enseignement", `${money(sim.forfait_enseignement)} (minimum 12 participants)`], [s.depl_label, money(sim.deplacement)], ["Sous-total", money(sim.sous_total)], ["Enseignement par jeune", `${money(sim.cout_par_jeune)} · ${money(sim.cout_par_jeune_par_cours)} par cours`], ["Total par jeune, déplacement inclus", money(sim.cout_par_jeune_total)], ["Matériel phase 1 (séparé)", `${Math.round(sim.materiel[0])} à ${Math.round(sim.materiel[1])} $`]] : null;
  const dark = s.kind === "closing";
  const hasPhoto = s.photo && s.kind !== "list" && s.kind !== "team";
  const profile = s.kind === "profile";
  return (
    <Frame n={n} total={total} dark={dark} footer={s.kind === "budget" ? "Taxes, matériel et événements affichés séparément · proposition de travail — à valider" : s.sous && s.kind === "closing" ? s.sous : ""}>
      <div className="h-full flex flex-col">
        <div className="flex items-start justify-between gap-8">
          <div className="max-w-[1000px]">
            <div className={`${B} text-[15px] uppercase tracking-[.3em]`} style={{ color: dark ? GOLD : RED }}>{s.kicker}</div>
            <h1 className={`${T} mt-3 font-semibold leading-[1.05] ${s.kind === "closing" ? "text-[78px]" : "text-[60px]"}`} data-testid="amikobi-slide-title">{s.titre}</h1>
          </div>
          <LogoPlate />
        </div>
        <div className={`mt-8 flex-1 grid ${hasPhoto ? (profile ? "grid-cols-[1.5fr_0.75fr]" : "grid-cols-[1.15fr_1fr]") : "grid-cols-1"} gap-14 items-center min-h-0`}>
          <div>
            {s.kind === "team" && <div className="grid grid-cols-4 gap-8" data-testid="amikobi-team-grid">{(s.equipe || []).map((c) => <div key={c.nom} className="rounded-[24px] bg-white overflow-hidden shadow-[0_18px_50px_-30px_rgba(42,42,46,.35)]"><img src={c.photo} alt={c.nom} className="w-full h-[300px] object-cover object-top" /><div className="p-5"><div className={`${T} text-[24px] font-semibold leading-tight`}>{c.nom}</div><div className={`${B} text-[17px] mt-1`} style={{ color: RED }}>{c.role}</div><p className={`${B} text-[15px] mt-2 leading-snug`} style={{ color: "#5e5750" }}>{c.ligne}</p></div></div>)}</div>}
            {s.sous && s.kind !== "closing" && <p className={`${B} text-[26px] leading-snug`} style={{ color: dark ? "#d8d2c8" : "#5e5750" }}>{s.sous}</p>}
            {rows && <table className="mt-2 text-[22px] bg-white rounded-2xl overflow-hidden w-full" data-testid="amikobi-budget-table"><tbody>{rows.map(([a, b], k) => <tr key={a} className="border-b" style={{ borderColor: "#eee5d8" }}><td className={`px-6 py-3 ${k === 3 ? "font-bold" : ""}`} style={{ color: k === 3 ? RED : "#5e5750" }}>{a}</td><td className={`px-6 py-3 ${k === 3 ? "font-bold" : ""}`}>{b}</td></tr>)}</tbody></table>}
            {(s.puces || []).length > 0 && <ul className={`${B} mt-6 space-y-4 ${s.kind === "list" || s.kind === "closing" ? "text-[32px]" : profile ? "text-[22px]" : "text-[24px]"} leading-snug`} style={{ color: dark ? "#f5f1ea" : "#3d3933" }}>{s.puces.map((p, k) => <li key={k} className="flex gap-4"><span className="mt-[14px] h-3 w-3 rounded-full shrink-0" style={{ background: k === s.puces.length - 1 && s.kind !== "list" ? GOLD : RED }} />{p}</li>)}</ul>}
          </div>
          {hasPhoto && <PhotoSlot slot={s.photo} photos={photos} defaults={defaults} label={s.kicker} className={`w-full ${profile ? "h-[560px]" : s.photo === "reseau" ? "h-[540px]" : "h-[460px]"}`} fit={profile || s.photo === "reseau" ? "contain" : "cover"} />}
        </div>
      </div>
    </Frame>
  );
}

/** Présentation claire complète (13 diapositives) — structure fournie par le backend (light_deck), notes du présentateur par diapo. */
const mmss = (ms) => { const t = Math.max(0, Math.floor(ms / 1000)); return `${String(Math.floor(t / 60)).padStart(2, "0")}:${String(t % 60).padStart(2, "0")}`; };

/** Minuteur discret + mode répétition : chronomètre chaque diapo, cible = temps alloué / nb de diapos, bilan en fin de parcours. */
function useRehearsal(i, total) {
  const [startAt] = useState(() => Date.now());
  const [now, setNow] = useState(Date.now());
  const [target, setTarget] = useState(0);
  const [durations, setDurations] = useState({});
  const slideStart = useRef(Date.now());
  const prev = useRef(i);
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);
  useEffect(() => {
    if (prev.current !== i) {
      const spent = Date.now() - slideStart.current;
      setDurations((d) => ({ ...d, [prev.current]: (d[prev.current] || 0) + spent }));
      slideStart.current = Date.now(); prev.current = i;
    }
  }, [i]);
  const perSlide = target ? (target * 60000) / total : 0;
  return { elapsed: now - startAt, onSlide: now - slideStart.current, target, setTarget, perSlide, durations };
}

export function LightDeck({ data, onClose }) {
  const deck = data.light_deck || [];
  const [i, setI] = useState(0);
  const [notes, setNotes] = useState(false);
  const [bilan, setBilan] = useState(false);
  const total = deck.length;
  const reh = useRehearsal(i, total);
  const over = reh.target && reh.elapsed > reh.target * 60000;
  const slideOver = reh.perSlide && reh.onSlide > reh.perSlide;
  const startRehearsal = () => { const m = window.prompt("Temps alloué pour toute la présentation (minutes) :", String(reh.target || 20)); if (m && Number(m) > 0) setTarget(Number(m)); };
  const setTarget = reh.setTarget;
  const [saved, setSaved] = useState(false);
  const [remote, setRemote] = useState(false);
  const lastRemote = useRef(null);
  useEffect(() => {
    if (!remote) return undefined;
    const poll = async () => { try { const r = await api.get("/amikobi/rencontre/telecommande"); if (r.data.updated_at && r.data.updated_at !== lastRemote.current) { lastRemote.current = r.data.updated_at; setI(Math.min(Math.max(r.data.index, 0), total - 1)); } } catch { /* silencieux */ } };
    poll(); const t = setInterval(poll, 1500); return () => clearInterval(t);
  }, [remote, total]);
  const saveRun = async () => {
    const durations = Object.fromEntries(deck.map((d, k) => [d.key, Math.round((reh.durations[k] || 0) + (k === i ? reh.onSlide : 0))]));
    try { await api.post("/amikobi/rencontre/repetitions", { target_min: reh.target || 0, total_ms: Math.round(reh.elapsed), durations }); setSaved(true); toast.success("Passage enregistré — compare-le dans l'onglet Rencontre"); } catch { toast.error("Enregistrement impossible"); }
  };
  useEffect(() => {
    const k = (e) => { if (e.key === "ArrowRight" || e.key === " ") setI((x) => Math.min(x + 1, total - 1)); if (e.key === "ArrowLeft") setI((x) => Math.max(x - 1, 0)); if (e.key === "Escape") onClose(); if (e.key.toLowerCase() === "n") setNotes((v) => !v); if (e.key.toLowerCase() === "r") setBilan((v) => !v); };
    window.addEventListener("keydown", k); document.documentElement.requestFullscreen?.().catch(() => {});
    [IIEM, MUAY, DOJOVA, ...Object.values(data.photos_defaults || {}), ...Object.values(data.photos || {})].forEach((src) => { const im = new Image(); im.src = src; });
    return () => { window.removeEventListener("keydown", k); document.exitFullscreen?.().catch(() => {}); };
  }, [total, onClose, data.photos_defaults, data.photos]);
  if (!total) return null;
  const s = deck[i]; const props = { photos: data.photos, defaults: data.photos_defaults, n: i + 1, total };
  const slide = s.kind === "cover" ? <Cover {...props} /> : s.kind === "benefits" ? <Benefits {...props} /> : s.kind === "progression" ? <Progression {...props} /> : <Generic s={s} {...props} sim={data.simulation_resultat} eff={data.rencontre?.simulation?.effectif} />;
  return (
    <div className="fixed inset-0 z-[100] flex flex-col" style={{ background: "#111" }} data-testid="amikobi-light-deck" data-slide={s.key}>
      <Stage onClick={() => setI((x) => Math.min(x + 1, total - 1))}>{slide}</Stage>
      {notes && <div className="px-6 py-3 text-sm border-t" style={{ background: "#1c1c1f", color: "#e9e4dc", borderColor: "#333" }} data-testid="amikobi-presenter-notes"><div className="text-[11px] uppercase tracking-[.2em] mb-1" style={{ color: GOLD }}>Notes du présentateur — {s.titre} (non projetées)</div><ul className="list-disc pl-5 space-y-0.5">{(s.notes || []).map((t, k) => <li key={k}>{t}</li>)}</ul></div>}
      {bilan && <div className="px-6 py-3 text-xs border-t max-h-48 overflow-auto" style={{ background: "#1c1c1f", color: "#e9e4dc", borderColor: "#333" }} data-testid="amikobi-rehearsal-bilan">
        <div className="flex items-center gap-3 mb-1"><span className="text-[11px] uppercase tracking-[.2em]" style={{ color: GOLD }}>Bilan de répétition — total {mmss(reh.elapsed)}{reh.target ? ` / cible ${reh.target} min (${mmss(reh.perSlide)} par diapo)` : ""}</span><button onClick={saveRun} disabled={saved} className="rounded-full border border-emerald-500 text-emerald-300 px-3 py-0.5 text-[11px] inline-flex items-center gap-1 disabled:opacity-50" data-testid="amikobi-rehearsal-save"><Save size={12} /> {saved ? "Passage enregistré" : "Enregistrer ce passage"}</button></div>
        <div className="grid grid-cols-4 gap-x-6 gap-y-0.5">{deck.map((d, k) => { const ms = (reh.durations[k] || 0) + (k === i ? reh.onSlide : 0); return <div key={d.key} className={`flex justify-between ${reh.perSlide && ms > reh.perSlide ? "text-red-300" : ""}`}><span className="truncate pr-2">{k + 1}. {d.titre}</span><span className="font-mono">{mmss(ms)}</span></div>; })}</div>
      </div>}
      <div className="flex items-center justify-between px-6 py-3 text-xs" style={{ background: "#111", color: "#8c877f" }}>
        <span className="flex items-center gap-3">
          <span className={`font-mono rounded px-2 py-0.5 ${over ? "bg-red-900/60 text-red-200" : slideOver ? "bg-amber-900/60 text-amber-200" : "bg-zinc-900"}`} title="Temps sur cette diapo · temps total" data-testid="amikobi-timer">{mmss(reh.onSlide)} · {mmss(reh.elapsed)}{reh.target ? ` / ${reh.target} min` : ""}</span>
          <span>{i + 1} / {total} — {s.titre} · ← → · N notes · R répétition · Échap</span>
        </span>
        <div className="flex gap-2">
          <button onClick={() => setRemote((v) => !v)} className={`rounded-full border px-3 py-1.5 ${remote ? "border-sky-400 text-sky-300" : "border-zinc-700"}`} title="Suivre la télécommande du téléphone (/app/amikobi-telecommande)" data-testid="amikobi-remote-toggle">📱 {remote ? "Télécommande active" : "Télécommande"}</button>
          <button onClick={() => (reh.target ? setBilan((v) => !v) : startRehearsal())} className={`rounded-full border px-3 py-1.5 inline-flex items-center gap-1 ${reh.target ? "border-emerald-500 text-emerald-300" : "border-zinc-700"}`} data-testid="amikobi-rehearsal-toggle"><Timer size={14} /> {reh.target ? (bilan ? "Masquer le bilan" : "Bilan") : "Répétition"}</button>
          <button onClick={() => setNotes((v) => !v)} className={`rounded-full border px-3 py-1.5 inline-flex items-center gap-1 ${notes ? "border-amber-400 text-amber-300" : "border-zinc-700"}`} data-testid="amikobi-notes-toggle"><StickyNote size={14} /> Notes</button>
          <button onClick={() => setI(Math.max(i - 1, 0))} className="rounded-full border border-zinc-700 p-1.5"><ChevronLeft size={16} /></button>
          <button onClick={() => setI(Math.min(i + 1, total - 1))} className="rounded-full border border-zinc-700 p-1.5" data-testid="amikobi-light-next"><ChevronRight size={16} /></button>
          <button onClick={onClose} className="rounded-full border border-zinc-700 p-1.5" data-testid="amikobi-light-close"><X size={16} /></button>
        </div>
      </div>
    </div>
  );
}

export const MODEL_SLIDES = [
  { key: "cover", titre: "Couverture", C: Cover, notes: ["Accueillir, se présenter brièvement (parcours personnel avec ses propres mots, photo approuvée).", "L'Institut porte la formation ; DOJOVA soutient l'organisation et le suivi des apprentissages.", "Message : des histoires différentes, une volonté commune — donner aux jeunes des occasions d'apprendre, de se dépasser et de prendre leur place."] },
  { key: "benefits", titre: "Bénéfices", C: Benefits, notes: ["Physique : coordination, équilibre, endurance, maîtrise. Attention : écoute, mémorisation, s'arrêter au signal.", "Confiance et relations : objectifs adaptés, coopération, respect.", "Limite à dire clairement : pistes encourageantes dans la recherche, pas une garantie de résultats scolaires."] },
  { key: "progression", titre: "Progression", C: Progression, notes: ["Cycle complet : observer → objectifs → enseigner → répéter → évaluer → reconnaître → ajuster.", "Trois parcours sans hiérarchie : découverte et mieux-être · apprentissage · développement sportif volontaire.", "Outils de suivi prêts dans DOJOVA (groupes de jeunes, présences, repères pour observer les progrès, bulletins) — pas encore utilisés sur le terrain."] },
];

/** Toile 1600×900 mise à l'échelle pour tenir dans l'écran (projection 16:9, aucun débordement). */
export function Stage({ children, onClick }) {
  const ref = useRef(null);
  const [scale, setScale] = useState(0.5);
  useEffect(() => {
    const el = ref.current; if (!el) return;
    const fit = () => setScale(Math.min(el.clientWidth / CANVAS.w, el.clientHeight / CANVAS.h));
    fit(); const ro = new ResizeObserver(fit); ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return (
    <div ref={ref} className="relative flex-1 min-h-0 overflow-hidden flex items-center justify-center" onClick={onClick}>
      <div style={{ width: CANVAS.w * scale, height: CANVAS.h * scale }}>
        <div style={{ width: CANVAS.w, height: CANVAS.h, transform: `scale(${scale})`, transformOrigin: "top left" }}>{children}</div>
      </div>
    </div>
  );
}

/** Aperçu des 3 diapositives modèles (couverture, bénéfices, progression) — à valider avant de décliner toute la présentation. */
export function ModelSlides({ data, onClose }) {
  const [i, setI] = useState(0);
  useEffect(() => { [IIEM, MUAY, DOJOVA].forEach((src) => { const im = new Image(); im.src = src; }); }, []);
  const [notes, setNotes] = useState(false);
  const total = MODEL_SLIDES.length;
  useEffect(() => {
    const k = (e) => {
      if (e.key === "ArrowRight" || e.key === " ") setI((x) => Math.min(x + 1, total - 1));
      if (e.key === "ArrowLeft") setI((x) => Math.max(x - 1, 0));
      if (e.key === "Escape") onClose();
      if (e.key.toLowerCase() === "n") setNotes((v) => !v);
    };
    window.addEventListener("keydown", k); document.documentElement.requestFullscreen?.().catch(() => {});
    return () => { window.removeEventListener("keydown", k); document.exitFullscreen?.().catch(() => {}); };
  }, [total, onClose]);
  const s = MODEL_SLIDES[i]; const Comp = s.C;
  return (
    <div className="fixed inset-0 z-[100] flex flex-col" style={{ background: "#111" }} data-testid="amikobi-model-slides">
      <Stage onClick={() => setI((x) => Math.min(x + 1, total - 1))}><Comp photos={data.photos} defaults={data.photos_defaults} n={i + 1} total={total} /></Stage>
      {notes && (
        <div className="px-6 py-3 text-sm border-t" style={{ background: "#1c1c1f", color: "#e9e4dc", borderColor: "#333" }} data-testid="amikobi-presenter-notes">
          <div className="text-[11px] uppercase tracking-[.2em] mb-1" style={{ color: GOLD }}>Notes du présentateur — {s.titre} (non projetées)</div>
          <ul className="list-disc pl-5 space-y-0.5">{s.notes.map((t, k) => <li key={k}>{t}</li>)}</ul>
        </div>
      )}
      <div className="flex items-center justify-between px-6 py-3 text-xs" style={{ background: "#111", color: "#8c877f" }}>
        <span>Diapositive modèle {i + 1} / {total} — {s.titre} · ← → naviguer · N notes · Échap quitter · format 16:9</span>
        <div className="flex gap-2">
          <button onClick={() => setNotes((v) => !v)} className={`rounded-full border px-3 py-1.5 inline-flex items-center gap-1 ${notes ? "border-amber-400 text-amber-300" : "border-zinc-700"}`} data-testid="amikobi-notes-toggle"><StickyNote size={14} /> Notes</button>
          <button onClick={() => setI(Math.max(i - 1, 0))} className="rounded-full border border-zinc-700 p-1.5"><ChevronLeft size={16} /></button>
          <button onClick={() => setI(Math.min(i + 1, total - 1))} className="rounded-full border border-zinc-700 p-1.5" data-testid="amikobi-model-next"><ChevronRight size={16} /></button>
          <button onClick={onClose} className="rounded-full border border-zinc-700 p-1.5" data-testid="amikobi-model-close"><X size={16} /></button>
        </div>
      </div>
    </div>
  );
}
